/**
 * useAuthGuard - Authentication routing guard
 *
 * Tracks auth state changes (login/logout/refresh) and redirects
 * the user to the appropriate view (Entry or Canvas).
 * Clears localStorage on login/logout transitions.
 *
 * Extracted from App.tsx lines 242–357
 */

import { useEffect, useRef } from "react";

import { STORAGE_KEYS } from "../app_constants";

interface UseAuthGuardParams {
  session: { user: { id: string } | null } | null;
  loading: boolean;
  currentView: "entry" | "canvas";
  handleBackToEntry: () => void;
}

/**
 * Restore canvas elements/appState from sessionStorage backup to localStorage.
 * Used after OAuth redirect when localStorage might have been cleared.
 */
const restoreCanvasBackupIfNeeded = () => {
  try {
    const hasElements = localStorage.getItem(
      STORAGE_KEYS.LOCAL_STORAGE_ELEMENTS,
    );
    const hasAppState = localStorage.getItem(
      STORAGE_KEYS.LOCAL_STORAGE_APP_STATE,
    );

    // Only restore if localStorage is missing data (e.g., cleared during redirect)
    if (!hasElements || !hasAppState) {
      const backupElements = sessionStorage.getItem(
        "ideaweave:canvasBackup:elements",
      );
      const backupAppState = sessionStorage.getItem(
        "ideaweave:canvasBackup:appState",
      );

      if (backupElements && !hasElements) {
        localStorage.setItem(
          STORAGE_KEYS.LOCAL_STORAGE_ELEMENTS,
          backupElements,
        );
      }
      if (backupAppState && !hasAppState) {
        localStorage.setItem(
          STORAGE_KEYS.LOCAL_STORAGE_APP_STATE,
          backupAppState,
        );
      }

      if (import.meta.env.DEV) {
        console.log(
          "[IdeaWeave] Restored canvas data from sessionStorage backup",
        );
      }
    }
  } catch (e) {
    console.error("[IdeaWeave] Failed to restore canvas backup:", e);
  }
};

export const useAuthGuard = ({
  session,
  loading,
  currentView,
  handleBackToEntry,
}: UseAuthGuardParams) => {
  // Track whether we've seen the initial auth load complete
  // This prevents treating page refresh as a "login" event
  const hasInitialAuthLoadedRef = useRef(false);
  const prevSessionUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    // Wait for auth to finish loading before we start tracking
    if (loading) {
      return;
    }

    const currentUserId = session?.user?.id ?? null;
    const prevUserId = prevSessionUserIdRef.current;

    // First time auth finishes loading - handle initial state
    if (!hasInitialAuthLoadedRef.current) {
      hasInitialAuthLoadedRef.current = true;
      prevSessionUserIdRef.current = currentUserId;
      if (import.meta.env.DEV) {
        console.log(
          "[IdeaWeave] Initial auth loaded, user:",
          currentUserId ? "logged in" : "not logged in",
        );
        console.log("[IdeaWeave] Current view:", currentView);
      }

      // Non-logged-in user on canvas: only allow if refreshing active work
      // or following a direct canvas link (collaboration/shared).
      // Otherwise redirect to Entry so the landing page is shown.
      if (!currentUserId && currentView === "canvas") {
        const navEntry = performance.getEntriesByType?.("navigation")?.[0] as
          | PerformanceNavigationTiming
          | undefined;
        const isPageReload = navEntry?.type === "reload";

        const isDirectCanvasAccess =
          window.location.hash.includes("#room=") ||
          window.location.hash.includes("#json=") ||
          window.location.search.includes("id=");

        if (isPageReload || isDirectCanvasAccess) {
          if (import.meta.env.DEV) {
            console.log(
              "[IdeaWeave] Non-logged-in user on canvas (refresh/direct link) - staying",
            );
          }
        } else {
          if (import.meta.env.DEV) {
            console.log(
              "[IdeaWeave] Non-logged-in user on canvas (new navigation) - redirecting to Entry",
            );
          }
          handleBackToEntry();
          return;
        }
      }

      // Logged-in user on canvas: distinguish refresh vs. return visit
      // - Refresh: stay on canvas (user explicitly refreshed, keep their view)
      // - Return from auth (quota flow): stay on canvas, preserve work
      // - New navigation (closed tab, came back, or new tab): go to Entry
      if (currentUserId && currentView === "canvas") {
        // Check if user just registered from the canvas quota-exceeded flow
        // (flag set by AIWeaverSidebar before opening auth modal)
        const returnToCanvas = sessionStorage.getItem(
          "ideaweave:returnToCanvasAfterAuth",
        );
        if (returnToCanvas) {
          if (import.meta.env.DEV) {
            console.log(
              "[IdeaWeave] User registered from canvas (OAuth redirect) - staying on canvas",
            );
          }
          // Restore canvas data from backup if localStorage was cleared
          restoreCanvasBackupIfNeeded();
          // Don't remove flag here - useProjectManager also needs to read it
          return;
        }

        const navEntry = performance.getEntriesByType?.("navigation")?.[0] as
          | PerformanceNavigationTiming
          | undefined;
        const isPageReload = navEntry?.type === "reload";

        if (isPageReload) {
          // User refreshed - stay on canvas, preserve localStorage so content restores
          if (import.meta.env.DEV) {
            console.log(
              "[IdeaWeave] Logged-in user refreshed on canvas - staying on canvas",
            );
          }
        } else {
          // User returned (new tab, closed and reopened, etc.) - go to Entry
          if (import.meta.env.DEV) {
            console.log(
              "[IdeaWeave] Logged-in user returned to app - redirecting to Entry",
            );
          }
          try {
            localStorage.removeItem(STORAGE_KEYS.LOCAL_STORAGE_ELEMENTS);
            localStorage.removeItem(STORAGE_KEYS.LOCAL_STORAGE_APP_STATE);
            localStorage.removeItem("ideaweave:lastView");
            sessionStorage.removeItem("ideaweave:lastView");
          } catch (error) {
            console.error("[IdeaWeave] Failed to clear localStorage:", error);
          }
          handleBackToEntry();
        }
      }
      return;
    }

    // User just logged in (was null, now has user ID)
    if (!prevUserId && currentUserId) {
      // Check if user just registered from the canvas quota-exceeded flow
      // (flag set by AIWeaverSidebar before opening auth modal)
      const returnToCanvas =
        currentView === "canvas" &&
        sessionStorage.getItem("ideaweave:returnToCanvasAfterAuth");

      if (returnToCanvas) {
        if (import.meta.env.DEV) {
          console.log(
            "[IdeaWeave] User registered from canvas (OTP) - staying on canvas with preserved work",
          );
        }
        // Don't clear localStorage or redirect - keep canvas state intact
        // Don't remove flag here - useProjectManager also needs to read it
        prevSessionUserIdRef.current = currentUserId;
        return;
      }

      if (import.meta.env.DEV) {
        console.log("[IdeaWeave] User logged in - resetting to entry view");
      }
      // Clear local storage to start fresh
      try {
        localStorage.removeItem(STORAGE_KEYS.LOCAL_STORAGE_ELEMENTS);
        localStorage.removeItem(STORAGE_KEYS.LOCAL_STORAGE_APP_STATE);
        localStorage.removeItem("ideaweave:lastView");
        sessionStorage.removeItem("ideaweave:lastView");
      } catch (error) {
        console.error(
          "[IdeaWeave] Failed to clear localStorage on login:",
          error,
        );
      }
      // Go to entry screen - user can then create new or select existing project
      handleBackToEntry();
    }

    // User just logged out
    if (prevUserId && !currentUserId) {
      if (import.meta.env.DEV) {
        console.log("[IdeaWeave] User logged out - resetting to entry view");
      }
      // Clear local storage
      try {
        localStorage.removeItem(STORAGE_KEYS.LOCAL_STORAGE_ELEMENTS);
        localStorage.removeItem(STORAGE_KEYS.LOCAL_STORAGE_APP_STATE);
        localStorage.removeItem("ideaweave:lastView");
        sessionStorage.removeItem("ideaweave:lastView");
      } catch (error) {
        console.error(
          "[IdeaWeave] Failed to clear localStorage on logout:",
          error,
        );
      }
      handleBackToEntry();
    }

    prevSessionUserIdRef.current = currentUserId;
  }, [session, loading, handleBackToEntry, currentView]);
};
